import { Util } from '../../../../utils/utils';
import { Constants } from '../../../../constants/constants';
import { Remote } from '../../../../api/remote';
import { ApiConstant } from '../../../../constants/api_constants';
import { NAV_PATH } from '../../../../router/constants';
import { useNavigate, useSearchParams } from 'react-router';

// 타입 정의
interface FaqDetail {
	faqId: string;
	faqCtgryId: string;
	faqTitl: string;
	faqRplyCtnt: string;
	nextFaqId?: string;
	prevFaqId?: string;
	nextFaqTitl?: string;
	prevFaqTitl?: string;
}

interface FaqListItem {
	faqId: string;
	faqTitl: string;
	faqCtgryNm: string;
}

export const useFaqDetail = () => {
	const navigate = useNavigate();
	const [params] = useSearchParams();

	const faqDetailInit = (): void => {
		faqDetailScrollReset();
		faqDetailGetParams();
	};

	// 리스트 스크롤 위치값 제거
	const faqDetailScrollReset = (): void => {
		const scrollTop = parseFloat(sessionStorage.getItem('faqScrollTop') || '');
		if (!Util.isNullOrEmpty(scrollTop)) {
			sessionStorage.removeItem('faqScrollTop');
		}
	};

	// 파라미터 가져오기
	const faqDetailGetParams = (): void => {
		const faqId = params.get('faqId');
		const faqCtgryId = params.get('faqCtgryId');
		if (!Util.isNullOrEmpty(faqId) && !Util.isNullOrEmpty(faqCtgryId)) {
			faqDetailGetData(faqId || '', faqCtgryId || '');
		}
	};

	// 카테고리 리스트
	const faqCateList = (list: FaqListItem[]): void => {
		const faqCtgryId = params.get('faqCtgryId');
		const cate = list.length !== 0 ? list[0].faqCtgryNm : Constants.empty;
		const paramCate = Util.isEmpty(decodeURI(params.get('cate') || ''));

		const navListElement = document.querySelector('.faq-nav-list');
		if (!navListElement) return;

		// 기존 내용 초기화
		navListElement.innerHTML = '';

		// 리스트 생성 및 이벤트 추가
		for (let i = 0; i < list.length; i++) {
			const link = `${NAV_PATH.CUSTOMER_SUPPORT.FAQ_DETAIL}?faqId=${list[i].faqId}&faqCtgryId=${faqCtgryId}&cate=${paramCate}`;

			// 요소 생성
			const liElement = document.createElement('li');
			const aElement = document.createElement('a');

			// 링크 텍스트 및 속성 설정
			aElement.textContent = list[i].faqTitl;
			aElement.setAttribute('href', link);

			// 클릭 이벤트 추가
			aElement.addEventListener('click', e => {
				e.preventDefault(); // 기본 동작 방지
				navigate(link); // React Router의 navigate 함수 호출
			});

			// li > a 구조 생성
			liElement.appendChild(aElement);
			navListElement.appendChild(liElement);
		}

		// const link = `/views/faq.html?cate=${paramCate}&srchTxt=&page=1`;
		const link = `${NAV_PATH.CUSTOMER_SUPPORT.FAQ}`;

		document.querySelector('.faq-nav-more')?.setAttribute('to', link);

		const faqQuickTitle = document.getElementById('faq_quick_title');
		if (faqQuickTitle) {
			faqQuickTitle.innerHTML = paramCate === Constants.empty ? Constants.all_kr : paramCate;
		}
	};

	// 상세 정보 입력
	const faqDetailSetInfo = (content: FaqDetail): void => {
		const title = document.querySelector('.board-detail .detail-head .title');
		if (title) {
			title.innerHTML = content.faqTitl;
		}

		const faqRplyCtnt = document.querySelector('.board-detail .detail-body .text-box');
		if (faqRplyCtnt) {
			faqRplyCtnt.innerHTML = content.faqRplyCtnt;
		}

		const nextBtn = document.querySelector('.detail-next-btn') as HTMLAnchorElement;

		const nextBtnTitle = document.querySelector('.detail-next-btn .title');
		if (nextBtnTitle) {
			faqDetailSetPrevNext(content, nextBtn, nextBtnTitle, true);
		}

		const prevBtn = document.querySelector('.detail-prev-btn') as HTMLAnchorElement;
		const prevBtnTitle = document.querySelector('.detail-prev-btn .title');
		if (prevBtnTitle) {
			faqDetailSetPrevNext(content, prevBtn, prevBtnTitle, false);
		}

		faqDetailShowContents();
	};

	// 이전 다음글 셋팅
	const faqDetailSetPrevNext = (content: FaqDetail, btn: HTMLAnchorElement, title: Element, isNext: boolean): void => {
		const className = isNext ? 'first' : 'last';
		const faqId = isNext ? content.nextFaqId : content.prevFaqId;
		const faqCtgryId = params.get('faqCtgryId');
		const cate = Util.isEmpty(params.get('cate'));
		const page = Util.isNullOrEmpty(params.get('page')) ? 1 : params.get('page');
		if (!Util.isNullOrEmpty(faqId)) {
			const link = `${NAV_PATH.CUSTOMER_SUPPORT.FAQ_DETAIL}?faqId=${faqId}&faqCtgryId=${faqCtgryId}&cate=${cate}&page=${page}`;
			btn.setAttribute('to', link);

			btn.addEventListener('click', e => {
				e.preventDefault();
				navigate(link);
			});

			title.innerHTML = isNext ? content.nextFaqTitl || '' : content.prevFaqTitl || '';
			title.classList.remove(className);
		} else {
			title.innerHTML = isNext ? '다음글이 없습니다.' : '이전글이 없습니다.';
			title.classList.add(className);
		}
	};

	// 상세 데이터 조회
	const faqDetailGetData = async (faqId: string, faqCtgryId: string): Promise<void> => {
		const cate = decodeURI(params.get('cate') || '');
		const allYn = Util.isNullOrEmpty(cate) || params.get('cate') === Constants.all_kr ? Constants.yes : Constants.no;
		const param = { allYn: allYn };
		const response = await Remote.Api.get(`${ApiConstant.mgmtFaq.detail}${faqId}/${faqCtgryId}`, param);
		if (response.code !== Constants.apiSuccess) {
			alert(response.message);
			return;
		}

		if (Util.isNullOrEmpty(response.content.detailFaq)) {
			alert('게시글이 존재하지 않습니다.');
			navigate(-1);
		} else {
			faqDetailSetInfo(response.content.detailFaq);
			faqCateList(response.content.faqListByCtgry);
		}
	};

	// 리스트 페이지로 가기
	const faqDetailClickGoList = (): void => {
		const page = Util.isEmpty(params.get('page'));
		const cate = Util.isEmpty(params.get('cate'));
		// location.href = `/views/faq.html?cate=${cate}&page=${page}`;
		navigate(`${NAV_PATH.CUSTOMER_SUPPORT.FAQ}?cate=${cate}&page=${page}`);
	};

	// 컨텐츠 보여주기
	const faqDetailShowContents = (): void => {
		document.querySelector('.faq .sub-content')?.classList.remove('hidden');
		document.querySelector('.footer')?.classList.remove('hidden');
	};

	(() => {
		faqDetailInit();
	})();

	return { faqDetailClickGoList };
};
